import React, { useEffect, useMemo, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import SearchIcon from '@mui/icons-material/Search'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonIcon from '@mui/icons-material/Person'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import EditIcon from '@mui/icons-material/Edit'
import HospitalIcon from '../../../icons/HospitalIcon'
import AgencyIcon from '../../../icons/AgencyIcon'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { PageCustomizationDTO, PageCustomizationStatus } from '../../../models/PageCustomization.model'
import PageCustomizationsService from '../../../services/PageCustomizations/PageCustomizationsService'
import { SnackbarContext } from '../../../providers/SnackbarContext'
import { formatRelativeDate } from '../../../utils/formatRelativeDate'

// ── Field order for diff ──
const fieldOrder: Array<keyof PageCustomizationDTO['customization']> = [
  'name',
  'imageUrl',
  'address',
  'city',
  'state',
  'zipcode',
  'website',
  'supportTelephone',
  'supportEmail',
  'latitude',
  'longitude',
]

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  imageUrl: 'Image',
  address: 'Address',
  city: 'City',
  state: 'State',
  zipcode: 'ZIP Code',
  website: 'Website',
  supportTelephone: 'Phone',
  supportEmail: 'Email',
  latitude: 'Latitude',
  longitude: 'Longitude',
}

// ── Status configuration ──
const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; gradient: string; icon: JSX.Element; bgLight: string; chipColor: 'warning' | 'success' | 'error' }
> = {
  pending: {
    label: 'Pending Review',
    color: '#ED6C02',
    gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
    icon: <HourglassEmptyIcon />,
    bgLight: '#FFF3E0',
    chipColor: 'warning',
  },
  approved: {
    label: 'Approved',
    color: '#2E7D32',
    gradient: 'linear-gradient(135deg, #66BB6A 0%, #43A047 100%)',
    icon: <CheckCircleIcon />,
    bgLight: '#E8F5E9',
    chipColor: 'success',
  },
  rejected: {
    label: 'Rejected',
    color: '#D32F2F',
    gradient: 'linear-gradient(135deg, #EF5350 0%, #E53935 100%)',
    icon: <CancelIcon />,
    bgLight: '#FFEBEE',
    chipColor: 'error',
  },
}

const getChangedFields = (item: PageCustomizationDTO) =>
  fieldOrder.filter((k) => {
    const orig: any = item.original
    const cust: any = item.customization
    const originalKey = k === 'imageUrl' ? 'imageURL' : k
    const proposed = cust[k]
    const original = orig?.[originalKey]
    if (proposed == null) return false
    // For latitude/longitude, compare with limited precision to avoid floating-point noise
    if (k === 'latitude' || k === 'longitude') {
      const p = Number(proposed)
      const c = Number(original)
      if (isNaN(p) && isNaN(c)) return false
      if (isNaN(p) || isNaN(c)) return true
      return Math.abs(p - c) > 0.0001
    }
    return String(proposed) !== String(original ?? '')
  })

// ══════════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════════
const AdminPageCustomizations = (): JSX.Element => {
  const service = useMemo(() => new PageCustomizationsService(), [])
  const [isAgency, setIsAgency] = useState(false)
  const [allItems, setAllItems] = useState<PageCustomizationDTO[]>([])
  const [status, setStatus] = useState<PageCustomizationStatus>('pending')
  const [selected, setSelected] = useState<PageCustomizationDTO | null>(null)
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    AppLayoutContext.setPageName('Page Customizations')
  }, [])

  // Fetch ALL customizations in one call (no status filter)
  const load = async () => {
    setLoading(true)
    try {
      const data = await service.getPageCustomizations(isAgency)
      setAllItems(data)
    } catch (e: any) {
      SnackbarContext.show(e?.data?.message || e?.message || 'Failed to load customizations', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Only re-fetch when entity type changes (single API call)
  useEffect(() => {
    load()
    setSearchQuery('')
  }, [isAgency])

  // Reset search when status tab changes
  useEffect(() => {
    setSearchQuery('')
  }, [status])

  const perform = async (action: 'approved' | 'rejected') => {
    if (!selected) return
    setActionLoading(true)
    try {
      await service.updatePageCustomizationStatus(selected.id, action)
      SnackbarContext.show(`Customization ${action}`)
      setConfirmAction(null)
      setSelected(null)
      load() // re-fetch all
    } catch (e: any) {
      SnackbarContext.show(e?.data?.message || e?.message || `Failed to ${action}`, 'error')
    } finally {
      setActionLoading(false)
  }
  }

  // ── Counts per status (computed from allItems) ──
  const statusCounts = useMemo(
    () => ({
      pending: allItems.filter((i) => i.status === 'pending').length,
      approved: allItems.filter((i) => i.status === 'approved').length,
      rejected: allItems.filter((i) => i.status === 'rejected').length,
    }),
    [allItems],
  )

  // ── Items for the currently selected status tab (recent first) ──
  const items = useMemo(
    () =>
      allItems
        .filter((i) => i.status === status)
        .sort((a, b) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()),
    [allItems, status],
  )

  // ── Filtered items (search within the active tab) ──
  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return items
    return items.filter(
      (item) =>
        item.organizationName.toLowerCase().includes(q) ||
        item.id.toString().includes(q) ||
        item.pageType.toLowerCase().includes(q) ||
        (item.original?.name || '').toLowerCase().includes(q),
    )
  }, [items, searchQuery])

  const activeConfig = STATUS_CONFIG[status] || STATUS_CONFIG.pending
  const entityLabel = isAgency ? 'Agency' : 'Hospital'

  return (
    <Box sx={{ pb: 4 }}>
      {/* ════════════ Hero Banner ════════════ */}
      <Box
        sx={{
          mb: 4,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          background: isAgency
            ? 'linear-gradient(135deg, #00695C 0%, #00897B 40%, #26A69A 100%)'
            : 'linear-gradient(135deg, #00363D 0%, #007C91 40%, #00ACC1 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            right: -60,
            top: -60,
            width: 260,
            height: 260,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.04)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: 100,
            bottom: -80,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.03)',
          }}
        />
        <Box sx={{ position: 'absolute', left: -20, bottom: -20, opacity: 0.04 }}>
          <TuneIcon sx={{ fontSize: 200 }} />
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.7,
              mb: 0.5,
              fontWeight: 500,
              letterSpacing: 1,
              textTransform: 'uppercase',
              fontSize: '0.75rem',
            }}
          >
            Admin
          </Typography>
          <Typography sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            Page Customizations
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85, fontSize: { xs: '0.85rem', sm: '0.95rem' }, mb: 2 }}>
            Review and manage proposed changes from organization admins
          </Typography>

          {/* Hospital / Agency toggle + count */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                display: 'inline-flex',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(8px)',
              }}
            >
              {([false, true] as const).map((agency) => (
                <Button
                  key={agency ? 'agency' : 'hospital'}
                  startIcon={agency ? <AgencyIcon /> : <HospitalIcon />}
                  onClick={() => setIsAgency(agency)}
                  size="small"
                  sx={{
                    px: 2.5,
                    py: 0.75,
                    borderRadius: 0,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    minHeight: 36,
                    color: isAgency === agency ? (isAgency ? '#00695C' : '#007C91') : 'rgba(255,255,255,0.85)',
                    bgcolor: isAgency === agency ? 'rgba(255,255,255,0.95)' : 'transparent',
                    '&:hover': {
                      bgcolor: isAgency === agency ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.1)',
                    },
                    transition: 'all 0.25s ease',
                  }}
                >
                  {agency ? 'Agencies' : 'Hospitals'}
                </Button>
              ))}
            </Box>

            {/* Total count badge */}
            {!loading && (
        <Chip
                label={`${allItems.length} total`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  height: 26,
                }}
        />
            )}
      </Stack>
        </Box>
      </Box>

      {/* ════════════ Status Stat Cards ════════════ */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(['pending', 'approved', 'rejected'] as const).map((s) => {
          const cfg = STATUS_CONFIG[s]
          const isActive = status === s
          const count = statusCounts[s]
          return (
            <Grid item xs={4} sm={4} key={s}>
              <Card
                onClick={() => setStatus(s)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 3,
                  border: isActive ? `2px solid ${cfg.color}` : '2px solid transparent',
                  boxShadow: isActive ? `0 4px 20px ${cfg.color}30` : '0 2px 8px rgba(0,0,0,0.06)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  overflow: 'hidden',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${cfg.color}20`,
                  },
                }}
              >
                {/* Top accent bar */}
                {isActive && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: cfg.gradient,
                    }}
                  />
                )}
                <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>
                  <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }}>
                    <Avatar
                      sx={{
                        width: { xs: 36, sm: 48 },
                        height: { xs: 36, sm: 48 },
                        bgcolor: isActive ? cfg.color : cfg.bgLight,
                        color: isActive ? '#fff' : cfg.color,
                        transition: 'all 0.3s ease',
                        display: { xs: 'none', sm: 'flex' },
                      }}
                    >
                      {React.cloneElement(cfg.icon, { sx: { fontSize: { xs: 18, sm: 24 } } })}
                    </Avatar>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: isActive ? cfg.color : '#263238',
                          lineHeight: 1.1,
                          fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        }}
                      >
                        {loading ? '—' : count}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.6rem', sm: '0.78rem' } }}
                      >
                        {cfg.label}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      {/* ════════════ Search Bar ════════════ */}
      <TextField
        fullWidth
        placeholder={`Search ${entityLabel.toLowerCase()} customizations by name, ID, or type…`}
        value={searchQuery}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2.5,
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            '& fieldset': { borderColor: 'rgba(0,124,145,0.12)' },
            '&:hover fieldset': { borderColor: 'rgba(0,124,145,0.3)' },
            '&.Mui-focused fieldset': { borderColor: '#007C91' },
          },
        }}
      />

      {/* ════════════ Results header ════════════ */}
      {!loading && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ color: '#546E7A', fontWeight: 600 }}>
              Showing
            </Typography>
            <Chip
              label={`${filteredItems.length} ${filteredItems.length === 1 ? 'result' : 'results'}`}
              size="small"
              sx={{
                bgcolor: activeConfig.bgLight,
                color: activeConfig.color,
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 24,
              }}
            />
          </Stack>
          <Chip
            icon={isAgency ? <AgencyIcon /> : <HospitalIcon />}
            label={isAgency ? 'Agencies' : 'Hospitals'}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500, fontSize: '0.75rem', height: 26, p: 1 }}
          />
        </Stack>
      )}

      {/* ════════════ Cards Grid ════════════ */}
      {loading ? (
        <Grid container spacing={2.5}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Skeleton variant="circular" width={44} height={44} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="60%" height={24} />
                      <Skeleton variant="text" width="40%" height={18} sx={{ mt: 0.5 }} />
                    </Box>
                    <Skeleton variant="rounded" width={80} height={26} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : filteredItems.length > 0 ? (
        <Grid container spacing={2.5}>
          {filteredItems.map((item) => {
            const changedFields = getChangedFields(item)
            const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending
            return (
              <Grid item xs={12} sm={6} key={item.id}>
                <Card
                  onClick={() => setSelected(item)}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderLeft: `5px solid ${cfg.color}`,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: `0 8px 24px ${cfg.color}18`,
                      '& .card-arrow': { opacity: 1, transform: 'translateX(0)' },
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                        {/* Organization name */}
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: '#1A2027',
                            mb: 0.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.3,
                          }}
                        >
                          {item.organizationName}
                        </Typography>

                        {/* Original entity name */}
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#78909C',
                            fontSize: '0.8rem',
                            display: 'block',
                            mb: 1.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.original?.name || 'Unknown entity'}
                        </Typography>

                        {/* Meta chips row */}
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                          <Chip
                            size="small"
                            label={`#${item.id}`}
                            sx={{
                              bgcolor: '#F5F5F5',
                              color: '#78909C',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 22,
                              '& .MuiChip-icon': { color: '#B0BEC5' },
                            }}
                          />
                          <Chip
                            size="small"
                            icon={isAgency ? <AgencyIcon /> : <HospitalIcon />}
                            label={item.pageType}
                            sx={{
                              bgcolor: '#F5F5F5',
                              color: '#546E7A',
                              fontWeight: 500,
                              fontSize: '0.7rem',
                              height: 22,
                              textTransform: 'capitalize',
                              '& .MuiChip-icon': { fontSize: 14, color: '#90A4AE' },
                            }}
                          />
                          {changedFields.length > 0 && (
                            <Chip
                              size="small"
                              icon={<EditIcon sx={{ fontSize: '14px !important' }} />}
                              label={`${changedFields.length} change${changedFields.length > 1 ? 's' : ''}`}
                              sx={{
                                bgcolor: '#E3F2FD',
                                color: '#1565C0',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 22,
                                '& .MuiChip-icon': { color: '#1565C0' },
                              }}
                            />
                          )}
                        </Stack>

                        {/* Status + timestamp */}
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Chip
                            size="small"
                            icon={React.cloneElement(cfg.icon, { sx: { fontSize: '14px !important' } })}
                            label={cfg.label}
                            sx={{
                              bgcolor: cfg.bgLight,
                              color: cfg.color,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 24,
                              '& .MuiChip-icon': { color: cfg.color },
                            }}
                          />
                          <Typography variant="caption" sx={{ color: '#B0BEC5', fontSize: '0.7rem' }}>
                            <AccessTimeIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.25 }} />
                            {formatRelativeDate(item.updatedAt || item.createdAt)}
                          </Typography>
                        </Stack>
                      </Box>

                      {/* Arrow */}
                      <ArrowForwardIcon
                        className="card-arrow"
                        sx={{
                          color: cfg.color,
                          opacity: 0.3,
                          fontSize: 20,
                          mt: 0.5,
                          transition: 'all 0.3s ease',
                          transform: 'translateX(-4px)',
                        }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      ) : (
        /* ── Empty State ── */
        <Box
          sx={{
            py: 8,
            px: 4,
            textAlign: 'center',
            background: 'linear-gradient(180deg, #FAFBFC 0%, #F0F4F8 100%)',
            borderRadius: 4,
            border: '2px dashed rgba(0,124,145,0.12)',
          }}
        >
          <Avatar
            sx={{
              width: 72,
              height: 72,
              bgcolor: activeConfig.bgLight,
              color: activeConfig.color,
              mx: 'auto',
              mb: 2.5,
            }}
          >
            {React.cloneElement(activeConfig.icon, { sx: { fontSize: 36 } })}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#263238', mb: 1 }}>
            {searchQuery.trim()
              ? 'No Results Found'
              : `No ${activeConfig.label} Customizations`}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#78909C', maxWidth: 420, mx: 'auto', lineHeight: 1.6 }}
          >
            {searchQuery.trim()
              ? `No ${entityLabel.toLowerCase()} customizations matching "${searchQuery.trim()}" were found.`
              : status === 'pending'
              ? `There are no pending ${entityLabel.toLowerCase()} customization requests awaiting review.`
              : status === 'approved'
              ? `No approved ${entityLabel.toLowerCase()} customizations yet.`
              : `No rejected ${entityLabel.toLowerCase()} customizations.`}
          </Typography>
        </Box>
      )}

      {/* ════════════ Detail Dialog ════════════ */}
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 16px 48px rgba(0,0,0,0.12)', overflow: 'hidden' },
        }}
      >
        {selected && (() => {
          const cfg = STATUS_CONFIG[selected.status] || STATUS_CONFIG.pending
          const changedFields = getChangedFields(selected)
          return (
            <>
              {/* Colored gradient header */}
              <Box
                sx={{
                  background: cfg.gradient,
                  color: 'white',
                  px: 3,
                  py: 2.5,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
                    <CompareArrowsIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      Review Customization
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
                      {selected.organizationName}
                    </Typography>
                  </Box>
                  <Chip
                    label={cfg.label}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                    }}
                  />
                </Stack>
              </Box>

              {/* Meta info strip */}
              <Box sx={{ px: 3, py: 1.5, bgcolor: '#FAFBFC', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    size="small"
                    label={`#${selected.id}`}
                    sx={{
                      bgcolor: '#F5F5F5',
                      color: '#78909C',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 22,
                      '& .MuiChip-icon': { color: '#B0BEC5' },
                    }}
                  />
                  <Chip
                    size="small"
                    icon={isAgency ? <AgencyIcon /> : <HospitalIcon />}
                    label={selected.pageType}
                    sx={{ bgcolor: '#F5F5F5', fontWeight: 500, fontSize: '0.72rem', height: 24, textTransform: 'capitalize', '& .MuiChip-icon': { fontSize: 14 } }}
                  />
                  <Chip
                    size="small"
                    icon={<PersonIcon sx={{ fontSize: '14px !important' }} />}
                    label={selected.organizationName}
                    sx={{ bgcolor: '#F5F5F5', fontWeight: 500, fontSize: '0.72rem', height: 24, '& .MuiChip-icon': { color: '#90A4AE' } }}
                  />
                  <Chip
                    size="small"
                    icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                    label={new Date(selected.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    sx={{ bgcolor: '#F5F5F5', fontWeight: 500, fontSize: '0.72rem', height: 24, '& .MuiChip-icon': { color: '#90A4AE' } }}
                  />
                  {changedFields.length > 0 && (
                    <Chip
                      size="small"
                      icon={<EditIcon sx={{ fontSize: '14px !important' }} />}
                      label={`${changedFields.length} field${changedFields.length > 1 ? 's' : ''} changed`}
                      sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600, fontSize: '0.72rem', height: 24, '& .MuiChip-icon': { color: '#1565C0' } }}
                    />
                  )}
                </Stack>
              </Box>

              {/* Diff / Applied table */}
              <DialogContent sx={{ p: 0 }}>
                {selected.status === 'approved' ? (() => {
                  // For approved: original already updated, show all non-null customization values
                  const cust: any = selected.customization
                  const displayFields = fieldOrder.filter((k) => {
                    const val = cust?.[k]
                    return val != null && String(val).trim() !== ''
                  })
                  return (
                    <>
                      <Box
                        sx={{
                          px: 3,
                          py: 1.5,
                          bgcolor: '#E8F5E9',
                          borderBottom: '1px solid #C8E6C9',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 18, color: '#2E7D32' }} />
                        <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 600 }}>
                          These changes have been approved and applied to the page.
                        </Typography>
                      </Box>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#FAFBFC' }}>
                            <TableCell sx={{ fontWeight: 700, color: '#455A64', width: 140, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.75 }}>
                              Field
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#2E7D32', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.75 }}>
                              Applied Value
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayFields.map((k) => {
                            const isImage = k === 'imageUrl'
                            const val = cust?.[k]
                            return (
                              <TableRow key={k} sx={{ '&:hover': { bgcolor: '#FAFBFC' }, '&:last-child td': { borderBottom: 0 } }}>
                                <TableCell sx={{ color: '#546E7A', fontWeight: 600, py: 2 }}>
                                  {FIELD_LABELS[k] || k}
                                </TableCell>
                                <TableCell sx={{ py: 2 }}>
                                  {isImage && val ? (
                                    <Box
                                      sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        border: '2px solid #C8E6C9',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s ease',
                                        '&:hover': { transform: 'scale(1.08)' },
                                      }}
                                      onClick={() => window.open(String(val), '_blank')}
                                    >
                                      <Box
                                        component="img"
                                        src={String(val)}
                                        alt="Applied"
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      />
                                    </Box>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{ color: '#2E7D32', fontWeight: 600, wordBreak: 'break-word', fontSize: '0.85rem' }}
                                    >
                                      {String(val)}
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            )
                          })}
                          {displayFields.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                No customization values recorded.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </>
                  )
                })() : changedFields.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#FAFBFC' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#455A64', width: 140, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.75 }}>
                          Field
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#D32F2F', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.75 }}>
                          Current
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#2E7D32', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.75 }}>
                          Proposed
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {changedFields.map((k) => {
                        const orig: any = selected.original
                        const cust: any = selected.customization
                        const originalKey = k === 'imageUrl' ? 'imageURL' : k
                        const isImage = k === 'imageUrl'
                        const origVal = orig?.[originalKey]
                        const propVal = cust?.[k]
                        return (
                          <TableRow key={k} sx={{ '&:hover': { bgcolor: '#FAFBFC' }, '&:last-child td': { borderBottom: 0 } }}>
                            <TableCell sx={{ color: '#546E7A', fontWeight: 600, py: 2 }}>
                              {FIELD_LABELS[k] || k}
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              {isImage && origVal ? (
                                <Box
                                  sx={{
                                    position: 'relative',
                                    width: 80,
                                    height: 80,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '2px solid #FFCDD2',
                                    opacity: 0.6,
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={String(origVal)}
                                    alt="Current"
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: 'rgba(211,47,47,0.15)',
                                    }}
                                  >
                                    <Typography sx={{ color: '#D32F2F', fontSize: '1.5rem', fontWeight: 800 }}>✕</Typography>
                                  </Box>
                                </Box>
                              ) : isImage ? (
                                <Typography variant="body2" sx={{ color: '#B0BEC5', fontStyle: 'italic', fontSize: '0.85rem' }}>
                                  No image
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#D32F2F',
                                    textDecoration: 'line-through',
                                    wordBreak: 'break-word',
                                    fontSize: '0.85rem',
                                  }}
                                >
                                  {String(origVal ?? '—')}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              {isImage && propVal ? (
                                <Box
                                  sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '2px solid #C8E6C9',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s ease',
                                    '&:hover': { transform: 'scale(1.08)' },
                                  }}
                                  onClick={() => window.open(String(propVal), '_blank')}
                                >
                                  <Box
                                    component="img"
                                    src={String(propVal)}
                                    alt="Proposed"
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#2E7D32',
                                    fontWeight: 600,
                                    wordBreak: 'break-word',
                                    fontSize: '0.85rem',
                                  }}
                                >
                                  {String(propVal)}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Avatar sx={{ width: 56, height: 56, bgcolor: '#F5F5F5', color: '#B0BEC5', mx: 'auto', mb: 2 }}>
                      <CompareArrowsIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Typography variant="subtitle1" sx={{ color: '#78909C', fontWeight: 600 }}>
                      No differences detected
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B0BEC5', mt: 0.5 }}>
                      The proposed changes are identical to the current data.
                    </Typography>
                  </Box>
                )}
              </DialogContent>

              {/* Actions */}
              <DialogActions
                sx={{
                  px: 3,
                  py: 2,
                  borderTop: '1px solid rgba(0,0,0,0.06)',
                  gap: 1,
                }}
              >
                {selected.status === 'pending' ? (
                <>
                    <Button
                      onClick={() => setSelected(null)}
                      sx={{ textTransform: 'none', fontWeight: 600, color: '#78909C' }}
                    >
                      Cancel
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<ThumbDownIcon />}
                      onClick={() => setConfirmAction('reject')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                      }}
                    >
                    Reject
                  </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<ThumbUpIcon />}
                      onClick={() => setConfirmAction('approve')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                        boxShadow: '0 2px 8px rgba(46,125,50,0.3)',
                      }}
                    >
                    Approve
                  </Button>
                </>
                ) : (
                  <Button
                    onClick={() => setSelected(null)}
                    variant="outlined"
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, ml: 'auto' }}
                  >
                    Close
                  </Button>
                )}
            </DialogActions>
          </>
          )
        })()}
      </Dialog>

      {/* ════════════ Confirmation Dialog ════════════ */}
      <Dialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 16px 48px rgba(0,0,0,0.12)', minWidth: 380 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {confirmAction === 'approve' ? 'Approve Changes?' : 'Reject Changes?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {confirmAction === 'approve'
              ? "This will apply the proposed changes to the page. The organization admin will be notified of the approval."
              : "This will reject the proposed changes. The organization admin will be notified that their changes were declined."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmAction(null)}
            disabled={actionLoading}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#78909C' }}
          >
            Cancel
          </Button>
          <Button
            color={confirmAction === 'approve' ? 'success' : 'error'}
            variant="contained"
            onClick={() => perform(confirmAction === 'approve' ? 'approved' : 'rejected')}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : confirmAction === 'approve' ? <ThumbUpIcon /> : <ThumbDownIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
            }}
          >
            {actionLoading ? 'Processing…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AdminPageCustomizations
